import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { categoriesService } from '../services/categories.service';
import type { CategoryCreatePayload, CategoryUpdatePayload } from '../types/categories.types';
import { toast } from 'sonner';

export function useCategories() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const userId = profile?.id || '';
  const queryClient = useQueryClient();

  const queryKey = ['inventory', 'categories', companyId];

  const query = useQuery({
    queryKey,
    queryFn: () => categoriesService.fetchCategories(companyId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CategoryCreatePayload) => categoriesService.createCategory(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Thêm danh mục thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi thêm danh mục');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CategoryUpdatePayload }) => 
      categoriesService.updateCategory(userId, companyId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Cập nhật danh mục thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi cập nhật danh mục');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesService.deleteCategory(userId, companyId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Xóa danh mục thành công');
    },
    onError: (err: any) => {
      console.error(err);
      toast.error(err.message || 'Lỗi khi xóa danh mục. Danh mục này có thể đang chứa vật tư.');
    },
  });

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    createCategory: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateCategory: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteCategory: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
